import { NgClass } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { AppUpdateService } from '@core/services/app-update.service';
import { AuthService } from '@core/services/auth.service';
import { TranslationBootService } from '@core/services/translation-boot.service';
import { UserGroupService } from '@core/services/user-group.service';
import { TranslateService } from '@ngx-translate/core';
import { BugReportButtonComponent } from '@shared/components/bug-report/bug-report-button.component';
import { EnvBadgeComponent } from '@shared/components/env-badge/env-badge.component';
import { IInfoDialogData, InfoDialogComponent } from '@shared/components/info-dialog/info-dialog.component';
import { NotificationsButtonComponent } from '@shared/components/notifications-button/notifications-button.component';
import { PageLoaderComponent } from '@shared/components/page-loader/page-loader.component';
import { combineLatest, EMPTY, merge, race, timer } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { ClarityService } from './core/services/clarity.service';
import { SidebarComponent } from './layout/sidebar/sidebar.component';

@Component({
  imports: [
    RouterOutlet,
    SidebarComponent,
    BugReportButtonComponent,
    EnvBadgeComponent,
    NotificationsButtonComponent,
    PageLoaderComponent,
    NgClass,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent {
  public readonly appUpdateService = inject(AppUpdateService);
  public readonly router = inject(Router);
  public readonly translationBoot = inject(TranslationBootService);

  private readonly authService = inject(AuthService);
  private readonly userGroupService = inject(UserGroupService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  // The two campaigns/demo/... entries mirror the real no-login routes above them - added
  // alongside those demo routes (see demo-campaign-details.routes.ts) so the sidebar stays hidden
  // there too. `.includes()` matching means the real entries alone don't cover these: the real
  // export/briefing URLs are `/campaigns/export/:id` and `/campaigns/briefing/creator/:id`, but the
  // demo ones have an extra `demo/` segment (`/campaigns/demo/export/:id`,
  // `/campaigns/demo/briefing/creator/:id`) that the real entries' substrings don't appear in.
  private readonly sidebarHiddenUrls = [
    '/auth/',
    '/maintenance',
    '/campaigns/briefing/creator/',
    '/invitations/',
    '/create-password',
    '/campaigns/export/',
    '/not-found',
    '/campaigns/demo/briefing/creator/',
    '/campaigns/demo/export/',
  ];

  private canDisplaySidebar = false;
  private accountTokenUsageWarningOpen = false;

  public get showSidebar(): boolean {
    const hiddenByUrl: boolean =
      this.sidebarHiddenUrls.some((url: string) => this.router.url.includes(url)) || this.router.url === '/';

    return this.canDisplaySidebar || !hiddenByUrl;
  }

  public get isCreatorBriefingRoute(): boolean {
    return ['/campaigns/briefing/creator/', '/campaigns/demo/briefing/creator/'].some((url: string) =>
      this.router.url.includes(url),
    );
  }

  public get addPadding(): boolean {
    return !this.router.url.includes('/auth/');
  }

  constructor(private readonly clarityService: ClarityService) {
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      const state: Record<string, unknown> | undefined = this.router.getCurrentNavigation()?.extras.state as
        | Record<string, unknown>
        | undefined;

      const effectiveState: Record<string, unknown> | undefined = state ?? (history.state as Record<string, unknown>);
      const canDisplayFromState = !!(effectiveState && effectiveState['creatorMode']);

      this.canDisplaySidebar = canDisplayFromState;
    });

    const initialState: Record<string, unknown> | undefined = history.state as Record<string, unknown>;
    this.canDisplaySidebar = !!(initialState && initialState['creatorMode']);

    // Initialize analytics - this will start tracking automatically
    // User identification will be handled after login via AuthService

    this.setupAccountTokenUsageWarning();
    this.setupBootLoaderRemoval();
  }

  /**
   * Removes the static boot loader inlined in index.html (see main.ts) once real content is
   * actually about to render: the first route navigation has finished - so <router-outlet> has
   * something in it, past any auth/permission guard redirects - AND translations are ready, so
   * this is the @if branch that's about to show rather than the blank gap before
   * showLoadingIndicator() flips on. Falls back to a bounded timeout so a hung guard or
   * resolver can't leave the boot loader on screen forever.
   */
  private setupBootLoaderRemoval(): void {
    const BOOT_LOADER_FALLBACK_MS = 15000;

    // take(1) rather than first(): if AppComponent is destroyed before navigation settles/
    // translations become ready (e.g. torn down mid-boot), toObservable() and the router's
    // events both complete with zero emissions on destroy - first() would throw EmptyError for
    // that, take(1) just quietly completes doing nothing, which is the correct behavior for a
    // best-effort cleanup side effect like this one.
    const navigationSettled$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      take(1),
    );
    const translationsReady$ = toObservable(this.translationBoot.ready).pipe(filter(Boolean), take(1));

    race(combineLatest([navigationSettled$, translationsReady$]), timer(BOOT_LOADER_FALLBACK_MS))
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        document.getElementById('boot-loader')?.remove();
      });
  }

  private setupAccountTokenUsageWarning(): void {
    const userGroupsChanged$ = toObservable(this.authService.userGroupsChanged).pipe(filter(Boolean));

    merge(userGroupsChanged$, this.authService.tokenRefreshed$)
      .pipe(
        filter(() => !!AuthService.currentDbUser),
        switchMap(() =>
          this.userGroupService
            .getCurrentUserGroup(new HttpParams().set('joins', 'subscription'))
            .pipe(catchError(() => EMPTY)),
        ),
        filter((userGroup) => (userGroup.tokenUsage ?? 0) >= 1.0),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.accountTokenUsageWarningOpen) {
          return;
        }

        this.accountTokenUsageWarningOpen = true;

        const dialogRef = this.dialog.open(InfoDialogComponent, {
          data: {
            closeText: this.translate.instant('shared.close'),
            message: this.translate.instant('dialogs.accountTokenUsageWarning.message'),
            title: this.translate.instant('dialogs.accountTokenUsageWarning.title'),
            type: 'warning',
          } satisfies IInfoDialogData,
          width: '400px',
        });

        dialogRef.afterClosed().subscribe(() => {
          this.accountTokenUsageWarningOpen = false;
        });
      });
  }
}
